import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTables } from "../../lib/useRealtimeTable";
import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import DealCallText from "../DealCallText";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Jennifer's QC View — matches C3 artifact design.
 *
 * KPI row: QC Queue | Approved Today | Rejected | Climb Auth
 * QC Review Queue: card grid with doc chips, SLA chips, approve/reject
 * Recently Approved: faded cards with "→ Waiting on Alex (Gate 2)"
 * Watching for Storms: deals Jennifer flagged "no date of loss yet"; the
 *   monitor-needs-date-of-loss cron pings the RM when a storm lands within
 *   1 mile at >=1.0" hail.
 */

const QC_COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,stage,days_in_stage,stage_entered_at,phase_entered_at,created_at,owner_id,drone_completed_date,imging_report_link,report_qc_status,report_qc_reviewed_at,qc_rejection_count,qc_rejection_reason,report_sla_met_jeremy,qc_sla_met_jennifer,sr_weather_report_storage_path,updated_at,needs_date_of_loss,needs_date_of_loss_flagged_at,needs_date_of_loss_note,needs_date_of_loss_last_alert_at,needs_date_of_loss_last_storm_at";

interface QCRow {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  stage: string | null;
  days_in_stage: number | null;
  stage_entered_at: string | null;
  phase_entered_at: string | null;
  created_at: string | null;
  owner_id: string | null;
  drone_completed_date: string | null;
  imging_report_link: string | null;
  report_qc_status: string | null;
  report_qc_reviewed_at: string | null;
  qc_rejection_count: number | null;
  qc_rejection_reason: string | null;
  report_sla_met_jeremy: boolean | null;
  qc_sla_met_jennifer: boolean | null;
  sr_weather_report_storage_path: string | null;
  updated_at: string | null;
  needs_date_of_loss: boolean | null;
  needs_date_of_loss_flagged_at: string | null;
  needs_date_of_loss_note: string | null;
  needs_date_of_loss_last_alert_at: string | null;
  needs_date_of_loss_last_storm_at: string | null;
}

function formatAddr(d: QCRow) {
  return [d.property_street, [d.property_city, d.property_state].filter(Boolean).join(", "), d.property_zip].filter(Boolean).join(" ");
}

/** SLA: 24 hours from entering qc_review (updated_at approximation) */
function slaInfo(d: QCRow): { label: string; cls: string; isOver: boolean } {
  if (!d.updated_at) return { label: "—", cls: "bg-slate-100 text-slate-500", isOver: false };
  const hoursAgo = Math.floor((Date.now() - new Date(d.updated_at).getTime()) / 3_600_000);
  const hoursLeft = 24 - hoursAgo;
  if (hoursLeft <= 0)
    return { label: `${Math.abs(hoursLeft)}hrs over`, cls: "bg-red-100 text-red-700", isOver: true };
  if (hoursLeft <= 6)
    return { label: `${hoursLeft}hrs left`, cls: "bg-amber-100 text-amber-700", isOver: false };
  return { label: `${hoursLeft}hrs left`, cls: "bg-emerald-100 text-emerald-700", isOver: false };
}

/** How long ago Jeremy submitted — approximate from updated_at */
function jeremySubmitted(d: QCRow): string {
  if (!d.updated_at) return "";
  const hrs = Math.floor((Date.now() - new Date(d.updated_at).getTime()) / 3_600_000);
  if (hrs < 1) return "just now";
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/** Relative time for approved cards */
function approvedAgo(d: QCRow): string {
  if (!d.report_qc_reviewed_at && !d.updated_at) return "";
  const dt = d.report_qc_reviewed_at ?? d.updated_at!;
  const hrs = Math.floor((Date.now() - new Date(dt).getTime()) / 3_600_000);
  if (hrs < 1) return "just now";
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface DocLink {
  deal_id: string;
  file_type: string | null;
  storage_path: string | null;
  file_name: string | null;
  created_at: string | null;
}

async function openDocByPath(storagePath: string) {
  // CRITICAL for mobile: open the tab synchronously inside the user gesture.
  // Mobile Safari/Chrome block window.open() that runs AFTER an await, because
  // the gesture context has expired. Open about:blank first, then redirect.
  const win = window.open("about:blank", "_blank");

  const { data, error } = await supabase.storage
    .from("deal-documents")
    .createSignedUrl(storagePath, 3600);

  if (error || !data?.signedUrl) {
    if (win && !win.closed) win.close();
    alert(`Couldn't open file: ${error?.message ?? "no signed URL"}`);
    return;
  }

  if (win && !win.closed) {
    win.location.href = data.signedUrl;
  } else {
    // Popup blocker still ate it — navigate the current tab instead.
    window.location.href = data.signedUrl;
  }
}

export default function JenniferQCView() {
  const { member } = useAuth();
  const [qcDeals, setQcDeals] = useState<QCRow[] | null>(null);
  const [approved, setApproved] = useState<QCRow[]>([]);
  const [watching, setWatching] = useState<QCRow[]>([]);
  const [climbCount, setClimbCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [docs, setDocs] = useState<DocLink[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [flaggingId, setFlaggingId] = useState<string | null>(null);
  const [flagNote, setFlagNote] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // Auto-refresh when Jeremy uploads or a deal stage changes
  useRealtimeTables(["deals", "deal_documents"], reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayISO = todayStart.toISOString();

      // "Watching for Storms" — every flagged deal regardless of stage. The
      // prior `.limit(5000)` was a no-op past the PostgREST 1000-row server
      // cap, so a major hailstorm putting >1000 deals on watch would silently
      // drop the oldest off the list. Paginate via .range() with a 5K safety
      // ceiling — at 5K, the iPad render is still fast and any reasonable
      // storm-watch list fits well under.
      const WATCH_CHUNK = 1000;
      const WATCH_SAFETY_CAP = 5000;
      async function fetchStormWatch(): Promise<any[]> {
        const all: any[] = [];
        for (let from = 0; from < WATCH_SAFETY_CAP; from += WATCH_CHUNK) {
          const { data, error } = await supabase
            .from("deals")
            .select(QC_COLS)
            .eq("needs_date_of_loss", true)
            .order("needs_date_of_loss_flagged_at", { ascending: false })
            .range(from, from + WATCH_CHUNK - 1);
          if (error) break;
          const chunk = (data ?? []) as any[];
          all.push(...chunk);
          if (chunk.length < WATCH_CHUNK) break;
        }
        return all;
      }

      const [
        { data: qc, error: e1 },
        { data: recent, error: e2 },
        watch,
        { count: climb },
        { data: rejToday },
        { data: mems },
        { data: docData },
      ] = await Promise.all([
        supabase
          .from("deals")
          .select(QC_COLS)
          .eq("stage", "qc_review")
          .order("updated_at", { ascending: true }),
        supabase
          .from("deals")
          .select(QC_COLS)
          .eq("stage", "report_approved")
          .order("updated_at", { ascending: false })
          .limit(20),
        fetchStormWatch(),
        supabase
          .from("deals")
          .select("*", { count: "exact", head: true })
          .eq("stage", "needs_climb_inspection"),
        supabase
          .from("deals")
          .select("id")
          .eq("report_qc_status", "rejected")
          .gte("report_qc_reviewed_at", todayISO),
        supabase
          .from("team_roster")
          .select("id,full_name")
          .eq("is_active", true),
        supabase
          .from("deal_documents")
          .select("deal_id,file_type,storage_path,file_name,created_at")
          .in("file_type", [
            "drone_report",
            "sr_weather_report",
            "noaa_weather_report",
            "storm_report",
            "companycam_report",
          ])
          .order("created_at", { ascending: false }),
      ]);
      // watch is the result of fetchStormWatch() which swallows errors and
      // returns whatever it loaded — so it never contributes an error to
      // surface here. e1 and e2 still cover the qc + recent queries.
      const firstErr = e1 ?? e2;
      if (firstErr) {
        if (!cancelled) setErr(firstErr.message);
        return;
      }
      if (!cancelled) {
        setQcDeals((qc ?? []) as QCRow[]);
        setApproved((recent ?? []) as QCRow[]);
        setWatching(watch as QCRow[]);
        setClimbCount(climb ?? 0);
        setRejectedCount(rejToday?.length ?? 0);
        const map: Record<string, string> = {};
        (mems ?? []).forEach((m) => { map[m.id] = m.full_name; });
        setMembers(map);
        setDocs((docData ?? []) as DocLink[]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Count approved today
  const approvedToday = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return approved.filter((d) => {
      const dt = d.report_qc_reviewed_at ?? d.updated_at;
      return dt && new Date(dt) >= todayStart;
    }).length;
  }, [approved]);

  async function handleApprove(dealId: string) {
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("deals")
        .update({
          stage: "report_approved",
          days_in_stage: 0,
          report_qc_status: "approved",
          report_qc_reviewed_at: now,
          qc_sla_met_jennifer: true,
        })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "QC Approved",
        description: "Jennifer approved the drone report — ready for ASM Gate 2",
        metadata: { from: "qc_review", to: "report_approved", reviewer: "jennifer" },
      });
      reload();
    } catch (e: any) {
      alert(`Approve failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  async function handleReject(dealId: string, currentCount: number) {
    if (!rejectReason.trim()) {
      alert("Please enter a rejection reason.");
      return;
    }
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("deals")
        .update({
          stage: "report_in_progress",
          days_in_stage: 0,
          report_qc_status: "rejected",
          report_qc_reviewed_at: now,
          qc_rejection_count: (currentCount ?? 0) + 1,
          qc_rejection_reason: rejectReason.trim(),
        })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "stage_change",
        title: "QC Rejected",
        description: `Jennifer rejected: ${rejectReason.trim()}`,
        metadata: {
          from: "qc_review",
          to: "report_in_progress",
          reason: rejectReason.trim(),
          rejection_count: (currentCount ?? 0) + 1,
        },
      });
      setRejectingId(null);
      setRejectReason("");
      reload();
    } catch (e: any) {
      alert(`Reject failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  /**
   * Set deals.needs_date_of_loss = true. The monitor-needs-date-of-loss cron
   * will start watching this property for new storms within 1 mile at >=1.0"
   * hail. flag-set timestamp resets the storm-since window so already-known
   * storms (which were on the original drone report) don't re-fire.
   */
  async function handleFlagNoDateOfLoss(dealId: string) {
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const now = new Date().toISOString();
      const note = flagNote.trim() || null;
      const { error } = await supabase
        .from("deals")
        .update({
          needs_date_of_loss: true,
          needs_date_of_loss_flagged_at: now,
          needs_date_of_loss_flagged_by: member?.id ?? null,
          needs_date_of_loss_note: note,
          // Reset prior storm/alert markers — fresh watch window starts now.
          needs_date_of_loss_last_alert_at: null,
          needs_date_of_loss_last_storm_at: null,
        })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "system_event",
        title: "Flagged: needs date of loss",
        description: note
          ? `Jennifer flagged this lead — no date of loss yet. Note: ${note}`
          : "Jennifer flagged this lead — no date of loss yet. Storm-watch is now active (1 mile / ≥1.0\" hail).",
        metadata: { source: "jennifer_qc_view", action: "flag_no_dol", note },
      });
      setFlaggingId(null);
      setFlagNote("");
      reload();
    } catch (e: any) {
      alert(`Flag failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  /** Clear the flag — RM has re-engaged or storm came in. */
  async function handleClearDateOfLossFlag(dealId: string) {
    setBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          needs_date_of_loss: false,
          needs_date_of_loss_flagged_at: null,
          needs_date_of_loss_flagged_by: null,
          needs_date_of_loss_note: null,
          needs_date_of_loss_last_alert_at: null,
          needs_date_of_loss_last_storm_at: null,
        })
        .eq("id", dealId);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: dealId,
        activity_type: "system_event",
        title: "Cleared: needs date of loss",
        description: "Storm-watch flag cleared.",
        metadata: { source: "jennifer_qc_view", action: "clear_no_dol" },
      });
      reload();
    } catch (e: any) {
      alert(`Clear failed: ${e.message ?? e}`);
    } finally {
      setBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!qcDeals)
    return <div className="text-slate-500 py-8 text-center text-sm">Loading QC queue…</div>;

  return (
    <div className="space-y-5">
      {/* ── KPI STATS ROW ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="QC Queue" value={qcDeals.length} color="text-amber-500" />
        <StatCard label="Approved Today" value={approvedToday} color="text-emerald-500" />
        <StatCard label="Rejected" value={rejectedCount} color="text-red-500" />
        <StatCard label="Climb Auth" value={climbCount} color="text-purple-500" />
      </div>

      {/* ── QC REVIEW QUEUE ── */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-[15px] font-semibold text-slate-900">QC Review Queue</h3>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">
            {qcDeals.length}
          </span>
        </div>

        {qcDeals.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-6 text-sm text-slate-500 text-center">
            QC queue clear — no reports awaiting review.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {qcDeals.map((d) => {
              const sla = slaInfo(d);
              const ownerName = d.owner_id ? members[d.owner_id]?.split(" ")[0] : null;
              const ownerLast = d.owner_id ? (members[d.owner_id]?.split(" ")[1]?.[0] ?? "") : "";
              const isRejecting = rejectingId === d.id;
              const isFlagging = flaggingId === d.id;
              const isBusy = !!busy[d.id];
              const isFlagged = !!d.needs_date_of_loss;
              // Source of truth: deal_documents rows (Jeremy uploads via Reports tab).
              // Docs are sorted newest-first; .find() picks the most recent of each type.
              const dealDocs = docs.filter((x) => x.deal_id === d.id);
              const droneDoc = dealDocs.find((x) => x.file_type === "drone_report");
              const weatherDoc = dealDocs.find(
                (x) => x.file_type === "sr_weather_report" || x.file_type === "noaa_weather_report",
              );
              const companyCamDoc = dealDocs.find((x) => x.file_type === "companycam_report");
              // Fall back to legacy columns for older deals that pre-date deal_documents
              const hasReport = !!droneDoc || !!d.imging_report_link;
              const hasWeather = !!weatherDoc || !!d.sr_weather_report_storage_path;
              const hasCompanyCam = !!companyCamDoc;

              return (
                <div
                  key={d.id}
                  className={
                    "bg-white border border-slate-200 rounded-lg p-3 space-y-2 " +
                    (sla.isOver ? "border-l-[3px] border-l-red-500" : "")
                  }
                >
                  {/* Top row: name + SLA chip */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[14px] text-slate-900">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {formatAddr(d)}
                          </a>
                        ) : (
                          formatAddr(d) || "—"
                        )}
                      </div>
                    </div>
                    <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 " + sla.cls}>
                      {sla.label}
                    </span>
                  </div>

                  {/* Meta chips: RM + Jeremy submitted */}
                  <div className="flex flex-wrap gap-1.5">
                    <DealAgeChips deal={d} />
                    {ownerName && (
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                        RM: {ownerName} {ownerLast}.
                      </span>
                    )}
                    <span className="text-[11px] px-2 py-0.5 rounded-md bg-purple-100 text-purple-700">
                      Jeremy submitted {jeremySubmitted(d)}
                    </span>
                  </div>

                  {/* Doc chips */}
                  <div className="flex flex-wrap gap-1.5">
                    {hasReport ? (
                      droneDoc?.storage_path ? (
                        <button
                          type="button"
                          onClick={() => openDocByPath(droneDoc.storage_path!)}
                          title={droneDoc.file_name ?? "Open drone report"}
                          className="text-[11px] px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 transition cursor-pointer"
                        >
                          Drone Report
                        </button>
                      ) : (
                        <a
                          href={d.imging_report_link!}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 transition"
                        >
                          Drone Report
                        </a>
                      )
                    ) : (
                      <span className="text-[11px] px-2.5 py-1 rounded-md bg-red-100 text-red-700 inline-flex items-center gap-1">
                        Drone Report missing
                      </span>
                    )}
                    {hasWeather ? (
                      weatherDoc?.storage_path ? (
                        <button
                          type="button"
                          onClick={() => openDocByPath(weatherDoc.storage_path!)}
                          title={weatherDoc.file_name ?? "Open weather report"}
                          className="text-[11px] px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 transition cursor-pointer"
                        >
                          Weather
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openDocByPath(d.sr_weather_report_storage_path!)}
                          title="Open weather report"
                          className="text-[11px] px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 transition cursor-pointer"
                        >
                          Weather
                        </button>
                      )
                    ) : (
                      <span className="text-[11px] px-2.5 py-1 rounded-md bg-red-100 text-red-700 inline-flex items-center gap-1">
                        Weather missing
                      </span>
                    )}
                    {hasCompanyCam && companyCamDoc?.storage_path ? (
                      <button
                        type="button"
                        onClick={() => openDocByPath(companyCamDoc.storage_path!)}
                        title={companyCamDoc.file_name ?? "Open CompanyCam report"}
                        className="text-[11px] px-2.5 py-1 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 inline-flex items-center gap-1 transition cursor-pointer"
                      >
                        CompanyCam
                      </button>
                    ) : (
                      <span className="text-[11px] px-2.5 py-1 rounded-md bg-red-100 text-red-700 inline-flex items-center gap-1">
                        CompanyCam missing
                      </span>
                    )}
                    {isAddressLinkable(d) && (
                      <a
                        href={mapsHref(d)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] px-2.5 py-1 rounded-md bg-white text-slate-600 border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1 transition"
                      >
                        Map
                      </a>
                    )}
                  </div>

                  {/* "Storm-watch active" pill — visible whenever the flag is set,
                      regardless of which input mode the action bar is in. */}
                  {isFlagged && !isFlagging && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 text-[11px] text-amber-700 flex items-center gap-2">
                      <span>🌩 Watching for storms</span>
                      {d.needs_date_of_loss_note && (
                        <span className="text-amber-600/80 truncate" title={d.needs_date_of_loss_note}>
                          · {d.needs_date_of_loss_note}
                        </span>
                      )}
                      <button
                        onClick={() => handleClearDateOfLossFlag(d.id)}
                        disabled={isBusy}
                        className="ml-auto text-amber-600 hover:text-amber-800 underline disabled:opacity-50"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {/* Action buttons */}
                  {isRejecting ? (
                    <div className="flex items-center gap-1.5 pt-0.5">
                      <input
                        type="text"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Rejection reason for Jeremy…"
                        className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-teal-500"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleReject(d.id, d.qc_rejection_count ?? 0); }}
                      />
                      <button
                        onClick={() => handleReject(d.id, d.qc_rejection_count ?? 0)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-md bg-red-500 text-white text-[12px] font-medium hover:bg-red-600 transition disabled:opacity-50"
                      >
                        {isBusy ? "…" : "Confirm"}
                      </button>
                      <button
                        onClick={() => setRejectingId(null)}
                        className="text-[12px] text-slate-400 hover:text-slate-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : isFlagging ? (
                    <div className="flex items-center gap-1.5 pt-0.5">
                      <input
                        type="text"
                        value={flagNote}
                        onChange={(e) => setFlagNote(e.target.value)}
                        placeholder="Optional note (why no DOL?)…"
                        className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-500"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") handleFlagNoDateOfLoss(d.id); }}
                      />
                      <button
                        onClick={() => handleFlagNoDateOfLoss(d.id)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-md bg-amber-500 text-white text-[12px] font-medium hover:bg-amber-600 transition disabled:opacity-50"
                      >
                        {isBusy ? "…" : "Flag & watch"}
                      </button>
                      <button
                        onClick={() => { setFlaggingId(null); setFlagNote(""); }}
                        className="text-[12px] text-slate-400 hover:text-slate-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      <button
                        onClick={() => handleApprove(d.id)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-md bg-emerald-500 text-white text-[12px] font-medium hover:bg-emerald-600 transition disabled:opacity-50"
                      >
                        {isBusy ? "Approving…" : "✓ Approve"}
                      </button>
                      <button
                        onClick={() => { setRejectingId(d.id); setRejectReason(""); }}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-md bg-red-500 text-white text-[12px] font-medium hover:bg-red-600 transition disabled:opacity-50"
                      >
                        ✗ Reject
                      </button>
                      {!isFlagged && (
                        <button
                          onClick={() => { setFlaggingId(d.id); setFlagNote(""); }}
                          disabled={isBusy}
                          className="px-3 py-1.5 rounded-md bg-amber-100 text-amber-700 border border-amber-200 text-[12px] font-medium hover:bg-amber-200 transition disabled:opacity-50"
                          title="Mark this lead as needing a date of loss; the system will watch for new storms within 1 mile (≥1.0″ hail) and ping the RM"
                        >
                          🌩 No date of loss yet
                        </button>
                      )}
                      <button className="px-3 py-1.5 rounded-md bg-white text-slate-600 border border-slate-300 text-[12px] font-medium hover:bg-slate-50 transition">
                        Notes
                      </button>
                      <DealLink id={d.id}>
                        <span className="px-3 py-1.5 rounded-md bg-white text-slate-600 border border-slate-300 text-[12px] font-medium hover:bg-slate-50 inline-block cursor-pointer transition">
                          View all docs
                        </span>
                      </DealLink>
                      <div className="ml-auto">
                        <DealCallText dealId={d.id} homeownerName={d.homeowner_name} homeownerPhone={d.homeowner_phone} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── RECENTLY APPROVED ── */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-[15px] font-semibold text-slate-900">Recently Approved</h3>
          {approvedToday > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-700">
              {approvedToday} today
            </span>
          )}
        </div>

        {approved.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500 text-center">
            No recently approved reports.
          </div>
        ) : (
          <div className="space-y-2">
            {approved.map((d) => {
              return (
                <div key={d.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 opacity-70">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[14px] text-slate-900">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {formatAddr(d)}
                          </a>
                        ) : (
                          formatAddr(d) || "—"
                        )}
                      </div>
                      <div className="mt-1">
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                          → Waiting on Alex (Gate 2)
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-lg bg-purple-100 text-purple-700 shrink-0">
                      Approved {approvedAgo(d)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── WATCHING FOR STORMS ── */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-[15px] font-semibold text-slate-900">🌩 Watching for Storms</h3>
          {watching.length > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700">
              {watching.length}
            </span>
          )}
          <span className="text-[11px] text-slate-400">
            ≥1.0″ hail within 1 mile · scans every 6h
          </span>
        </div>
        {watching.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500 text-center">
            No leads currently waiting on a storm match.
          </div>
        ) : (
          <div className="space-y-2">
            {watching.map((d) => {
              const ownerName = d.owner_id ? members[d.owner_id] ?? "—" : "—";
              const flaggedAgo = d.needs_date_of_loss_flagged_at
                ? Math.floor((Date.now() - new Date(d.needs_date_of_loss_flagged_at).getTime()) / 86_400_000)
                : null;
              const lastAlert = d.needs_date_of_loss_last_alert_at
                ? new Date(d.needs_date_of_loss_last_alert_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                : null;
              return (
                <div key={d.id} className="bg-white border border-amber-200 border-l-[3px] border-l-amber-500 rounded-lg px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[14px] text-slate-900">
                        <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5">
                        {isAddressLinkable(d) ? (
                          <a
                            href={mapsHref(d)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline hover:text-slate-700"
                          >
                            {formatAddr(d)}
                          </a>
                        ) : (
                          formatAddr(d) || "—"
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                          RM: {ownerName.split(" ")[0]} {ownerName.split(" ")[1]?.[0] ?? ""}.
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">
                          Stage: {d.stage ?? "—"}
                        </span>
                        {flaggedAgo !== null && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-700">
                            Flagged {flaggedAgo === 0 ? "today" : `${flaggedAgo}d ago`}
                          </span>
                        )}
                        {lastAlert && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700">
                            Storm match · alerted {lastAlert}
                          </span>
                        )}
                      </div>
                      {d.needs_date_of_loss_note && (
                        <div className="mt-1.5 text-[12px] text-slate-600 italic">
                          “{d.needs_date_of_loss_note}”
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleClearDateOfLossFlag(d.id)}
                      disabled={!!busy[d.id]}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-white text-amber-700 border border-amber-300 hover:bg-amber-50 shrink-0 disabled:opacity-50"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
      <div className={`text-xl font-extrabold ${color}`}>{value}</div>
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
